import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sun, Moon, LogOut, Smartphone, User as UserIcon, ChevronRight, Gem, BellRing, Loader2, Megaphone, Ticket } from 'lucide-react';
import { fieldAlertsSupported, enableFieldAlerts, disableFieldAlerts } from '../lib/fieldAlerts.js';
import { auth, affiliate as affiliateApi } from '../lib/api.js';
import { useSession } from '../lib/session.jsx';
import { useTheme } from '../lib/theme.jsx';
import { platform } from '../lib/supabase.js';
import { Glass, Button, Tag, Heading, Field } from '../components/ui.jsx';

const VERSION = import.meta.env.VITE_APP_VERSION ?? '0.1.0';

export default function SettingsPage() {
  const { profile, isAdmin, device, refresh } = useSession();
  const { theme, set } = useTheme();
  const [alertBusy, setAlertBusy] = useState(false);
  const [alertMsg, setAlertMsg] = useState(null);
  const alertsOn = Boolean(device?.field_alerts);
  const [isCreator, setIsCreator] = useState(false);
  const [invite, setInvite] = useState('');
  const [inviteMsg, setInviteMsg] = useState(null);
  const [inviteBusy, setInviteBusy] = useState(false);
  useEffect(() => { affiliateApi.overview().then((o) => setIsCreator(Boolean(o))).catch(() => {}); }, []);
  // Invite codes are only claimable in an account's first week.
  const newAccount = profile?.created_at && Date.now() - new Date(profile.created_at).getTime() < 7 * 86_400_000;
  const claimInvite = async () => {
    setInviteBusy(true); setInviteMsg(null);
    try { await affiliateApi.claim(invite); setInviteMsg('Welcome bonus added.'); setInvite(''); await refresh(); }
    catch (e) { setInviteMsg(e.message); }
    finally { setInviteBusy(false); }
  };

  const toggleAlerts = async () => {
    setAlertBusy(true); setAlertMsg(null);
    try {
      if (alertsOn) { await disableFieldAlerts(device); setAlertMsg('Field alerts are off.'); }
      else {
        const perm = await enableFieldAlerts(device);
        const always = perm?.location === 'always' || perm?.location === 'granted' || perm?.always === 'granted';
        setAlertMsg(always ? 'On. Your phone will tell you when you walk into a field.'
                           : 'On — but location is not set to "Always", so alerts only work while the app is open. Change it in Settings → Location.');
      }
      await refresh();
    } catch (e) { setAlertMsg(e.message); }
    finally { setAlertBusy(false); }
  };

  return (
    <div className="space-y-4 md:space-y-5">
      <header className="rise rise-1 pt-2 pb-2 md:pb-4">
        <p className="eyebrow">Preferences</p>
        <h1 className="display text-[2.6rem] md:text-[3.4rem] mt-2">
          <span className="display-i lit">Settings</span>.
        </h1>
      </header>

      <Glass className="rise rise-2">
        <Heading eyebrow="Appearance" title="Light or dark" />
        <div className="grid grid-cols-2 gap-2" data-noswipe>
          {[['light', Sun, 'Light'], ['dark', Moon, 'Dark']].map(([key, Icon, label]) => (
            <button key={key} type="button" onClick={() => set(key)} className="chip py-3.5 px-4" data-on={theme === key}>
              <span className="flex items-center gap-2 text-sm font-semibold"><Icon size={14} /> {label}</span>
            </button>
          ))}
        </div>
      </Glass>

      {fieldAlertsSupported() && (
        <Glass className="rise rise-2">
          <Heading eyebrow="Field alerts" title="Know when you are in a field"
                   action={<Tag tone={alertsOn ? 'active' : 'neutral'} dot>{alertsOn ? 'On' : 'Off'}</Tag>} />
          <p className="text-sm -mt-2 leading-relaxed" style={{ color: 'var(--text-2)' }}>
            Your phone watches the nearest fields, even with the app closed. Walk into one and a notification
            asks whether to connect — nothing connects by itself. Leaving a field you are connected to disconnects it.
            Needs location set to <strong>Always</strong>.
          </p>
          <div className="mt-4 flex items-center gap-3 flex-wrap" data-noswipe>
            <Button variant={alertsOn ? 'quiet' : 'primary'} disabled={alertBusy || !device} onClick={toggleAlerts}>
              {alertBusy ? <Loader2 size={14} className="animate-spin" /> : <BellRing size={14} />}
              {alertsOn ? 'Turn off' : 'Turn on for this device'}
            </Button>
            {alertMsg && <span className="text-xs" style={{ color: 'var(--text-3)' }}>{alertMsg}</span>}
          </div>
        </Glass>
      )}

      <Glass className="rise rise-3">
        <Heading eyebrow="Account" title={profile?.full_name ?? 'You'}
                 action={isAdmin ? <Tag tone="warn">Keeper</Tag> : null} />
        <p className="text-sm -mt-2" style={{ color: 'var(--text-3)' }}>{profile?.email}</p>
        <div className="mt-5 space-y-2">
          <Row to="/profile" icon={UserIcon} label="Profile, crystal and history" />
          <Row to="/profile#devices" icon={Smartphone} label="Devices"
               hint={device?.label ? `This one: ${device.label}` : null} />
          <Row to="/subscription" icon={Gem} label="Covenant and tokens" />
          {isCreator && <Row to="/affiliate" icon={Megaphone} label="Creator programme" hint="Your invite code and earnings" />}
        </div>
        <Button variant="quiet" className="mt-5" onClick={() => auth.signOut()}>
          <LogOut size={14} /> Sign out
        </Button>
      </Glass>

      {newAccount && (
        <Glass className="rise rise-4">
          <Heading eyebrow="Invited by a creator?" title="Enter their code" />
          <div className="flex gap-2 items-start" data-noswipe>
            <div className="flex-1"><Field placeholder="e.g. K7PX2M" autoCapitalize="characters" autoComplete="off"
                   value={invite} onChange={(e) => setInvite(e.target.value)} hint={inviteMsg} /></div>
            <Button variant="glass" disabled={inviteBusy || invite.trim().length < 4} onClick={claimInvite}>
              {inviteBusy ? <Loader2 size={14} className="animate-spin" /> : <Ticket size={14} />} Apply
            </Button>
          </div>
        </Glass>
      )}

      <p className="rise rise-5 text-xs px-1" style={{ color: 'var(--text-3)' }}>
        Version {VERSION} · {platform()} · com.waserd.field
      </p>
    </div>
  );
}

function Row({ to, icon: Icon, label, hint }) {
  return (
    <Link to={to} className="glass-flat flex items-center gap-3 px-4 py-3 rounded-2xl transition-colors hover:bg-[var(--glass-2)]">
      <Icon size={16} style={{ color: 'var(--clay)' }} />
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium">{label}</span>
        {hint && <span className="block text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>{hint}</span>}
      </span>
      <ChevronRight size={15} style={{ color: 'var(--text-3)' }} />
    </Link>
  );
}
